import './MainMenuUserMenu.scss';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Dropdown, Menu, Popup } from 'semantic-ui-react';
import { switchAccounts, Logout } from '../auth/AuthService';
import { analyticsTrack } from '../helpers/AnalyticsUtility';
import { canIUse, AppFeatures, Features } from '../helpers/PermissionUtility';
import MainMenuUserIcon from './MainMenuUserIcon';
import { useNavigate } from 'react-router-dom';

export type MainMenuUserMenuProps = {
  companyName: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  userId: string;
  hasProfilePicture: boolean;
  isIndependentUser: boolean;
};

export default function MainMenuUserMenu(props: MainMenuUserMenuProps) {
  const { companyName, emailAddress, firstName, lastName, userId, hasProfilePicture, isIndependentUser } = props;
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const onProfileMenuItemClick = (to: string, analyticsButton: string) => {
    analyticsTrack('Profile Drop-down Button Clicked', { button: analyticsButton });
    navigate(to);
  };

  const onLogoutClick = async () => {
    analyticsTrack('Profile Drop-down Button Clicked', { button: 'Logout' });
    setIsOpen(false); //close menu so rerenders dont happen as we clean up state
    await Logout();
  };

  const onMenuClick = () => {
    setIsOpen(true);
    analyticsTrack('Profile Drop-down Button Clicked', { button: 'Icon' });
  };

  const userMenu = [
    <Menu.Item role="menuitem" key="username" title="View Profile" className="username" onClick={() => onProfileMenuItemClick('/profile', 'Username')}>
      <span>
        {firstName} {lastName}
      </span>
    </Menu.Item>,
    <Dropdown.Divider key="usernamedivider" />,
    isIndependentUser ? (
      <Menu.Item role="menuitem" key="company-name" className="no-hover">
        <div className="company-name">
          <div className="company-name-icon-wrapper">
            <svg width="100%" height="100%" viewBox="0 0 80 80" preserveAspectRatio="xMinYMin meet">
              <foreignObject width="100%" height="100%" xmlns="http://www.w3.org/1999/xhtml">
                <div>{(companyName || '').charAt(0).toUpperCase()}</div>
              </foreignObject>
            </svg>
          </div>
          <span className="company-name-text">{companyName}</span>
        </div>
      </Menu.Item>
    ) : null,
    canIUse(AppFeatures.ManageUsers) ? (
      <Menu.Item role="menuitem" key="usergroups" title="Users & Groups" onClick={() => onProfileMenuItemClick('/users', 'Users & Groups')}>
        <span>Users &amp; Groups</span>
      </Menu.Item>
    ) : null,
    canIUse(AppFeatures.ManageSubscription) ? (
      <Menu.Item role="menuitem" key="managesubs" title="Subscription" onClick={() => onProfileMenuItemClick('/subscription', 'Subscriptions & Billing')}>
        <span>{canIUse(Features.SubscriptionUIRefresh, false) ? 'Subscription' : 'Subscription & Billing'}</span>
      </Menu.Item>
    ) : null,
    <Menu.Item role="menuitem" key="profile" title="View Profile" onClick={() => onProfileMenuItemClick('/profile', 'My Profile')}>
      <span>My Profile</span>
    </Menu.Item>,
    canIUse(AppFeatures.ManageSettings) ? (
      <Menu.Item role="menuitem" key="settings" title="Settings" onClick={() => onProfileMenuItemClick('/settings', 'Settings')}>
        <span>Settings</span>
      </Menu.Item>
    ) : null,
    isIndependentUser ? (
      <Menu.Item
        role="menuitem"
        key="switch-account"
        onClick={() => {
          analyticsTrack('Profile Switch Account Button Clicked', { button: 'Switch Account' });
          switchAccounts();
        }}
      >
        <span>Switch Account</span>
      </Menu.Item>
    ) : null,
    <Menu.Item role="menuitem" key="logout" onClick={onLogoutClick}>
      <span>Logout</span>
    </Menu.Item>
  ].filter((item) => !!item);

  return (
    <Popup
      open={isOpen}
      onClose={() => setIsOpen(false)}
      trigger={
        <MainMenuUserIcon
          onClick={onMenuClick}
          emailAddress={emailAddress}
          firstName={firstName}
          lastName={lastName}
          userId={userId}
          hasProfilePicture={hasProfilePicture}
        />
      }
      className="main-menu-user-menu"
      position="bottom left"
      content={
        <Menu role="menu" vertical className="user-menu">
          {userMenu.map((menuItem) => menuItem)}
        </Menu>
      }
    />
  );
}
